import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    styled,
    Typography,
} from "@mui/material";
import { isEmpty, unionBy } from "lodash";
import { ChangeEvent, Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import PageTemplate from "../../components/template/page-template";
import { IListItem } from "../../interface/list-item";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { IWithClassName, IWithThemeProps } from "../../theme/default.theme";
import Columns from "./components/column-lists";
import CustomLists from "./components/custom-lists";
import Filters from "./components/filters";
import ListDownloadOption from "./components/list-download-options";
import { ListBuilderDownloadAs } from "./enums/download-options";
import { FormParams } from "./enums/form-params";
import { ListBuilderLists } from "./enums/list-options";
import { Steps } from "./enums/steps";
import { TolerancePeriod, ToleranceRange } from "./enums/tolerance";
import { IListBuilderDetailsBodyDto } from "./interface/dto/list-builder-details.dto";
import {
    IColumnsForm,
    ICustomListsForm,
    IError,
    IFiltersForm,
    IListDownloadOptionForm,
    IStepForm,
} from "./interface/form";
import {
    listBuilderGetDetailsAction,
    listBuilderInitAction,
    listBuilderSaveAction,
    resetSaveFlagAction,
} from "./redux/list-builder-form-page.action";
import { validate } from "./service/validation";

const RETAILSTAT_URL = process.env.REACT_APP_RETAILSTAT_URL ?? "";

const defaultFrom = {
    name: "",
    lists: ListBuilderLists.ALL,
    addSnapshots: false,
    downloadAs: ListBuilderDownloadAs.SINGLE_FILE,
    splitFiles: false,
    includeUpdatedOnly: false,
    sicCodes: [],
    naicsCodes: [],
    categories: [],
    listIds: [],
    addTrackedLists: false,
    useListColumns: false,
    columns: [],
    countryFilter: [],
    stateFilter: [],
    primaryNaicsCodesOnly: false,
    minLocationCount: undefined,
    maxLocationCount: undefined,
    excludeDistributors: false,
    snapshotDates: [],
    setFrequency: false,
    tolerance: 1,
    tolerancePeriod: TolerancePeriod.MONTHS,
    toleranceRange: ToleranceRange.BOTH,
    snapshotDate: "",
};

const defaultStepForm = {
    [Steps.FILTERS]: {
        countryFilter: defaultFrom.countryFilter,
    },
    [Steps.COLUMN_LISTS]: {
        useListColumns: defaultFrom.useListColumns,
        columns: defaultFrom.columns,
    },
    [Steps.CUSTOM_LISTS]: {
        naicsCodes: defaultFrom.naicsCodes,
    },
    [Steps.LIST_DOWNLOAD_OPTIONS]: {
        lists: defaultFrom.lists,
        downloadAs: defaultFrom.downloadAs,
        addSnapshots: defaultFrom.addSnapshots,
        includeUpdatedOnly: defaultFrom.includeUpdatedOnly,
        splitFiles: defaultFrom.splitFiles,
        name: defaultFrom.name,
        snapshotDates: defaultFrom.snapshotDates,
        tolerancePeriod: defaultFrom.tolerancePeriod,
        toleranceRange: defaultFrom.toleranceRange,
        tolerance: defaultFrom.tolerance,
        setFrequency: defaultFrom.setFrequency,
    },
};

const coreFields = [
    "list_id",
    "list_name",
    "address",
    "city",
    "state",
    "zip_code",
    "phone_number",
    "latitude",
    "longitude",
];

const Page = ({ className }: IWithClassName) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { listBuilderId } = useParams();
    const { listBuilder } = useAppSelector(({ listBuilder }) => listBuilder);
    const { listBuilderList } = useAppSelector(
        ({ listBuilder }) => listBuilder,
    );

    const [initForm, setInitForm] =
        useState<IListBuilderDetailsBodyDto>(defaultFrom);
    const [error, setError] = useState<IError>({});
    const [stepForm, setStepForm] = useState<IStepForm>(defaultStepForm);

    const getNodeValue = (value: Array<string | number | IListItem>) =>
        value.map((node) => (typeof node === "object" ? node.id : node));

    const canUseListColumns =
        !stepForm[Steps.LIST_DOWNLOAD_OPTIONS][FormParams.ADD_SNAPSHOTS] &&
        stepForm[Steps.LIST_DOWNLOAD_OPTIONS][FormParams.DOWNLOAD_AS] !==
            ListBuilderDownloadAs.SINGLE_FILE;

    const onSubmit = (run: boolean) => {
        const flatForm = {
            ...initForm,
            ...stepForm[Steps.LIST_DOWNLOAD_OPTIONS],
            ...stepForm[Steps.CUSTOM_LISTS],
            ...stepForm[Steps.COLUMN_LISTS],
            ...stepForm[Steps.FILTERS],
        };

        const requestForm = Object.keys(flatForm).reduce((acc, key) => {
            const value = flatForm[key as FormParams];
            return {
                ...acc,
                [key]: Array.isArray(value) ? getNodeValue(value) : value,
            };
        }, initForm);

        const errorObj = validate(requestForm);

        setError(errorObj);

        if (isEmpty(errorObj)) {
            dispatch(
                listBuilderSaveAction(
                    requestForm,
                    run,
                    listBuilderId ? Number(listBuilderId) : undefined,
                ),
            );
        }
    };

    useEffect(() => {
        if (stepForm[Steps.LIST_DOWNLOAD_OPTIONS][FormParams.ADD_SNAPSHOTS]) {
            setStepForm((elements) => ({
                ...elements,
                [Steps.LIST_DOWNLOAD_OPTIONS]: {
                    ...elements[Steps.LIST_DOWNLOAD_OPTIONS],
                    [FormParams.INCLUDE_UPDATED_ONLY]: false,
                },
            }));
        }
    }, [stepForm[Steps.LIST_DOWNLOAD_OPTIONS][FormParams.ADD_SNAPSHOTS]]);

    useEffect(() => {
        setStepForm((elements) => ({
            ...elements,
            [Steps.COLUMN_LISTS]: {
                ...elements[Steps.COLUMN_LISTS],
                [FormParams.USE_LIST_COLUMNS]:
                    canUseListColumns &&
                    stepForm[Steps.COLUMN_LISTS][FormParams.USE_LIST_COLUMNS],
            },
        }));
    }, [
        canUseListColumns,
        stepForm[Steps.COLUMN_LISTS][FormParams.USE_LIST_COLUMNS],
    ]);

    useEffect(() => {
        if (listBuilderId) {
            dispatch(listBuilderGetDetailsAction(Number(listBuilderId)));
        }

        return () => {
            dispatch(resetSaveFlagAction());
        };
    }, [listBuilderId, dispatch]);

    useEffect(() => {
        dispatch(listBuilderInitAction());
    }, []);

    useEffect(() => {
        if (listBuilder.data) {
            setInitForm(listBuilder.data);
        }

        if (listBuilder.saveError) {
            if (listBuilder.saveError.error === FormParams.NAME) {
                setError({
                    [FormParams.NAME]: listBuilder.saveError.message,
                });
            } else {
                setError({
                    main: listBuilder.saveError.message,
                });
            }
        }

        if (listBuilder.saveSuccess) {
            dispatch(resetSaveFlagAction());

            void navigate("/list-builder");
        }
    }, [listBuilder]);

    const onCheckBoxChange =
        (step: Steps, label: FormParams) =>
        (event: ChangeEvent<HTMLInputElement>) =>
            setStepForm((options) => ({
                ...options,
                [step]: {
                    ...options[step],
                    [label]: event.target.checked,
                },
            }));

    const onSelectChange =
        (step: Steps, label: FormParams) =>
        (
            selectedOptions:
                | Array<IListItem>
                | string
                | Array<string>
                | number
                | boolean,
        ) =>
            setStepForm((options) => ({
                ...options,
                [step]: { ...options[step], [label]: selectedOptions },
            }));

    const addRemoveCoreFields = ({
        currentTarget,
    }: ChangeEvent<HTMLInputElement>) =>
        setStepForm((options) => ({
            ...options,
            [Steps.COLUMN_LISTS]: {
                ...options[Steps.COLUMN_LISTS],
                [FormParams.COLUMNS]: currentTarget.checked
                    ? unionBy(
                          options[Steps.COLUMN_LISTS][FormParams.COLUMNS],
                          coreFields.map((value) => ({
                              id: value,
                              name: value,
                          })),
                          "id",
                      )
                    : options[Steps.COLUMN_LISTS][FormParams.COLUMNS]?.filter(
                          ({ id }) => !coreFields.includes(id),
                      ),
            },
        }));

    const onTextChange =
        (step: Steps, label: FormParams) =>
        (event: ChangeEvent<HTMLInputElement>) =>
            setStepForm((options) => ({
                ...options,
                [step]: {
                    ...options[step],
                    [label]: event.target ? event.target.value : "",
                },
            }));

    const onNumberChange =
        (step: Steps, label: FormParams) =>
        (event: ChangeEvent<HTMLInputElement>) => {
            const { value } = event.target;
            const numericValue = parseInt(value, 10);
            setStepForm((options) => {
                const updatedStep = {
                    ...(options[step] as Record<FormParams, any>),
                };
                if (isNaN(numericValue)) {
                    updatedStep[label] = null;
                } else {
                    updatedStep[label] = numericValue;
                }
                return {
                    ...options,
                    [step]: updatedStep,
                };
            });
        };

    const initStepForm = (
        step: Steps,
        form:
            | IFiltersForm
            | IListDownloadOptionForm
            | ICustomListsForm
            | IColumnsForm,
    ) => {
        setStepForm((state) => ({
            ...state,
            [step]: form,
        }));
    };

    const renderErrorMessage = (message: string) => {
        const contactUs = "Contact Us";
        if (message.indexOf(contactUs) >= 0) {
            return message
                .split(contactUs)
                .filter((item) => !!item.trim())
                .map((part) => (
                    <Fragment key={part}>
                        {part}
                        <a
                            className="error-link"
                            href={`${RETAILSTAT_URL}/contact-us`}
                        >
                            {contactUs}
                        </a>
                    </Fragment>
                ));
        } else {
            return message;
        }
    };

    return (
        <PageTemplate pageTitle="List Builder">
            <Grid className={className}>
                <Box className="header-box">
                    <Typography variant="h6" className="text-center">
                        {listBuilderId ? "EDIT" : "NEW"} LIST BUILDER
                    </Typography>
                    <Typography>
                        Export POI Data in bulk by selecting your desired
                        format, lists and fields below
                    </Typography>
                </Box>
                {error.main && (
                    <Alert severity="error">
                        {renderErrorMessage(error.main)}
                    </Alert>
                )}
                <ListDownloadOption
                    onTextChange={onTextChange}
                    onSelectChange={onSelectChange}
                    onCheckBoxChange={onCheckBoxChange}
                    form={stepForm[Steps.LIST_DOWNLOAD_OPTIONS]}
                    error={error}
                    initStepForm={initStepForm}
                />

                {stepForm[Steps.LIST_DOWNLOAD_OPTIONS].lists !==
                    ListBuilderLists.ALL && (
                    <>
                        <Typography variant="h6">
                            Step 1: Choose Lists
                        </Typography>
                        <CustomLists
                            onSelectChange={onSelectChange}
                            onCheckBoxChange={onCheckBoxChange}
                            form={stepForm[Steps.CUSTOM_LISTS]}
                            error={error}
                            initStepForm={initStepForm}
                        />
                    </>
                )}
                <Box className="step2-heading">
                    <Box className="step2-heading" flexDirection={"column"}>
                        <Typography variant="h6">
                            Step 2: Select and Arrange Column Order
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onChange={onCheckBoxChange(
                                        Steps.COLUMN_LISTS,
                                        FormParams.USE_LIST_COLUMNS,
                                    )}
                                    checked={stepForm.columns.useListColumns}
                                    disabled={!canUseListColumns}
                                />
                            }
                            label="Use field order from each original List"
                        />
                    </Box>
                    <Box className="step2-heading">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={coreFields.every((coreField) =>
                                        stepForm[Steps.COLUMN_LISTS]?.[
                                            FormParams.COLUMNS
                                        ]?.some((col) => col.id === coreField),
                                    )}
                                    onChange={addRemoveCoreFields}
                                />
                            }
                            label="Add Core Fields"
                        />
                        <Button
                            href="https://s3.amazonaws.com/aggdata_docs/AggData+Dictionary.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={
                                <img
                                    src="/assets/book.svg"
                                    alt="Data Dictionary Icon"
                                    className="data-dictionary"
                                />
                            }
                            className="data-dictionary"
                        >
                            Data Dictionary
                        </Button>
                    </Box>
                </Box>

                <Columns
                    onSelectChange={onSelectChange}
                    form={stepForm[Steps.COLUMN_LISTS]}
                    error={error}
                    initStepForm={initStepForm}
                />
                <Typography variant="h6">Step 3: Apply Filters</Typography>
                <Filters
                    onSelectChange={onSelectChange}
                    form={stepForm[Steps.FILTERS]}
                    onCheckBoxChange={onCheckBoxChange}
                    initStepForm={initStepForm}
                    onNumberChange={onNumberChange}
                    error={error}
                />
                <FormControl className="button-cont">
                    {listBuilder.isLoading ||
                    listBuilderList.isTrackedListsLoading ? (
                        <CircularProgress />
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                color="secondary"
                                className="text-center button-style"
                                onClick={() => onSubmit(false)}
                            >
                                save list builder
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                className="text-center button-style"
                                onClick={() => onSubmit(true)}
                            >
                                save and run list builder
                            </Button>
                        </>
                    )}
                </FormControl>
            </Grid>
        </PageTemplate>
    );
};
export default styled(Page)(({ theme }: IWithThemeProps) => ({
    display: "grid",
    gap: 20,
    ".header-box": {
        display: "grid",
        gridTemplateColumns: "1fr 4fr",
        p: {
            color: theme?.palette.announcement.primaryColor,
            fontSize: 22,
        },
    },
    ".text-center": {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        textAlign: "center",
    },
    ".button-cont": {
        flexDirection: "row",
        gap: 20,
        justifyContent: "center",
    },
    ".MuiCircularProgress-svg": {
        color: theme?.palette.action.active,
    },
    ".step2-heading": {
        display: "flex",
        justifyContent: "space-between",
    },
    ".MuiAlert-icon svg": {
        color: theme?.palette.error.main,
    },
    ".data-dictionary": {
        color: theme?.palette.text.primary,
        fontSize: 16,
        textDecoration: "none",
        textTransform: "none",
    },
    ".error-link": {
        color: "inherit",
    },
}));
